import {Control, ScaleLine, defaults as defaultControls} from 'ol/control';
import React, {useState, useEffect, useRef} from 'react';
import {fromLonLat, transform} from 'ol/proj';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import TileLayer from 'ol/layer/Tile';
import {observer} from 'mobx-react';
import Point from 'ol/geom/Point';
import Feature from 'ol/Feature';
import Overlay from 'ol/Overlay';
import OSM from 'ol/source/OSM';
import View from 'ol/View';
import Map from 'ol/Map';
import 'ol/ol.css';

import {getCityName, getShortSpecName, getCountryName} from '../Trans.js';
import {useGlobalStorage} from '../mobx/GlobalStorage.js';
import {LIST_MAP_MAX_ELEM} from '../ListMap.js';
import {TT_SUBSPEC, TT_SPEC} from '../Util.js';
import {mapStyles} from '../MapStyles.js';
import {TAB_MAP} from './TaxCommon.js';
import {dStr, tStr} from '../Util.js';
import {specToMsg} from '../Main.js';
import {COMM_OK} from '../WsR2.js';

function MapObsPopup(props) {
	function closeClick(e) {
		props.proceedDetails(1);
	}

	return (
		<div ref={props.mapPopRef} className="overlay-container" id="obs-popup" onClick={closeClick} style={{backgroundColor: 'white', padding: 8, border: '2px solid #cccccc', borderRadius: 10, boxShadow: '2px 4px #888888'}}>
			{props.num > 1 ? <div className="overlay-text" id="obs-pos">{props.pos + 1 + ' / ' + props.num}</div> : null}
			<div className="overlay-text" id="obs-species" />
			<div className="overlay-text" id="obs-date" />
			<div className="overlay-text" id="obs-address" />
		</div>
	);
}

const StyleTglBtn = observer((props) => {
	const gStorage = useGlobalStorage();
	const leftPos = gStorage.taxons.mapWidth - 40;

	return (
		<button 
		    className='ol-custom ol-control' 
		    ref={props.mapCtrlRef}
		    style={{top: 8, left: leftPos, width: 32, height: 32}} 
		    onClick={props.tglStyle}>
			{'S' + props.style}
		</button>
	);
});

const TaxMap = observer(() => {
	const [clickFt, setClickFt] = useState({pos:0, fa:[]});
	const [resizeDone, setResizeDone] = useState(false);
	const gStorage = useGlobalStorage();
	const mapCtrlRef = useRef(null);
	const mapMapRef = useRef(null);
	const mapPopRef = useRef(null);

	useEffect(() => {
		const initialObsLayer = new VectorLayer({source: new VectorSource()});

		const initialMap = new Map({
			controls: defaultControls({rotate: false, zoom: false}).extend([new ScaleLine({units: 'metric'})]),

			layers: [
				new TileLayer({
					source: new OSM(),
				}),
				initialObsLayer,
			],
		
			/* Zoomaa Suomen kokoiseksi */
			view: new View({
					center: fromLonLat([27.138596, 65.441896]),
					zoom: 5,
				}),
			moveTolerance: 8
		});

		/* ---------------------------------------------------------- */

		initialMap.on('click', mapClick);

		gStorage.taxons.setMap(initialMap, initialObsLayer);

		/* ---------------------------------------------------------- */

		const h = window.innerHeight - 
			(document.getElementById('appbar') ? document.getElementById('appbar').clientHeight : 48) -
			(document.getElementById('taxonname') ? document.getElementById('taxonname').clientHeight : 56) - 
			(document.getElementById('taxontab') ? document.getElementById('taxontab').clientHeight : 48) -
			(document.getElementById('bottombar') ? document.getElementById('bottombar').clientHeight : 48);

		gStorage.taxons.setMapSize(window.innerWidth, h);

		window.addEventListener('resize', handleResize);

		return (() => {
			window.removeEventListener('resize', handleResize);
		});
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
// 		console.log('Map: useEffect, currents', mapMapRef.current, mapCtrlRef.current, mapPopRef.current);

		if (mapMapRef.current) {
			gStorage.taxons.map.setTarget(mapMapRef.current);

			if (mapCtrlRef.current) {
				while (gStorage.taxons.map.getControls().getLength() > 2) {
					gStorage.taxons.map.removeControl(gStorage.taxons.map.getControls().getArray()[2]);
				}

				gStorage.taxons.map.addControl(new Control({element: mapCtrlRef.current}));
			}

			if (mapPopRef.current) {
				while (gStorage.taxons.map.getOverlays().getLength() > 0) {
					gStorage.taxons.map.removeOverlay(gStorage.taxons.map.getOverlays().getArray()[0]);
				}

				gStorage.taxons.map.addOverlay(new Overlay({element: mapPopRef.current, id: 'obs'}));
			}
		}
		// eslint-disable-next-line
	}, [mapMapRef.current, mapCtrlRef.current, mapPopRef.current, gStorage.taxons.tab]);

	useEffect(() => {
		if (!gStorage.taxons.obsLayer)
			return;

		fillMap();
		// eslint-disable-next-line
	}, [gStorage.taxons.obsStyle]);

	useEffect(() => {
// 		console.log('useEffect, obslist', gStorage.taxons.obsLayer);

		if (!gStorage.taxons.obsLayer)
			return;

		if (gStorage.taxons.map.getOverlayById('obs')) 
			gStorage.taxons.map.getOverlayById('obs').setPosition(undefined);

		fillMap();

		updateViewport();
		// eslint-disable-next-line
	}, [gStorage.taxons.obslist]);

	useEffect(() => {
// 		console.log('useEffect: resizeDone', resizeDone);

		if (!gStorage.taxons.obsLayer) {
			setResizeDone(false);
			return;
		}

		if (resizeDone === false) {
			updateViewport();
		} else {
			setResizeDone(false);
		}
		// eslint-disable-next-line
	}, [resizeDone]);

	/* Update obs popup */
	useEffect(() => {
		if (!gStorage.taxons.map || !gStorage.taxons.map.getOverlayById('obs')) {
// 			console.log('useEffect: clikcFt null map!')
			return;
		}

		gStorage.taxons.map.getOverlayById('obs').setPosition(undefined);

		if (!clickFt.fa.length || clickFt.pos >= clickFt.fa.length)
			return;

		const obs = clickFt.fa[clickFt.pos].get('obs');
		if (obs) {
			let d_str = '', t_str;

			d_str = dStr(obs.start_t);
			t_str = tStr(obs.start_t);
			if (t_str.length > 0 && t_str !== '0:00')
				d_str += ' ' + t_str;

			gStorage.taxons.map.getOverlayById('obs').setPosition(clickFt.fa[clickFt.pos].coordinate);

			document.getElementById('obs-species').innerHTML = taxMapGetSpeciesName(gStorage.lists.speclist.list.find((taxon) => taxon.id === obs.spec_id)) + ', ' + obs.num;
			document.getElementById('obs-date').innerHTML = d_str;
			document.getElementById('obs-address').innerHTML = taxMapGetCityName(obs.city_id) + ', ' + obs.addr;
		}
		// eslint-disable-next-line
	}, [clickFt]);

	function updateViewport() {
		if (gStorage.taxons.obslist && gStorage.taxons.obslist.length > 0) {
			gStorage.taxons.map.getView().fit(gStorage.taxons.obsLayer.getSource().getExtent(), {
				size: gStorage.taxons.map.getSize(),
				maxZoom: 16,
				padding: [32, 32, 32, 32]});
		} else {
			gStorage.taxons.map.setView(
				new View({
					center: fromLonLat([27.138596, 65.441896]),
					zoom: 4
				})
			);
		}
	}

	function handleResize() {
		/* Availabilty depends on rendering order => q&d hard coded defaults! */
		const h = window.innerHeight - 
			(document.getElementById('appbar') ? document.getElementById('appbar').clientHeight : 48) -
			(document.getElementById('taxonname') ? document.getElementById('taxonname').clientHeight : 56) - 
			(document.getElementById('taxontab') ? document.getElementById('taxontab').clientHeight : 48) -
			(document.getElementById('bottombar') ? document.getElementById('bottombar').clientHeight : 48);

		gStorage.taxons.setMapSize(window.innerWidth, h);
		setResizeDone(true);
	}

	function fillMap() {
		let features = [];
	
		gStorage.taxons.obslist && gStorage.taxons.obslist.forEach((obs, ind) => {
			let tgtFeat = new Feature({
				geometry: new Point(transform([obs.long_obs / 1000000.0, obs.lat_obs / 1000000.0], 'EPSG:4326', 'EPSG:3857')),
				obs: obs
			});

			tgtFeat.setStyle(mapStyles[gStorage.taxons.obsStyle]);
			features.push(tgtFeat);
		});

		gStorage.taxons.obsLayer.setSource(
			new VectorSource({
				features: features,
			})
		)
	}

	function taxMapGetCityName(id) {
		const usr = gStorage.conn.user;
		const city = gStorage.lists.citylist.list.find(city => city.id === id);
		let a_str = '';

		if (city.country !== usr.def_country) {
			const country = gStorage.lists.countrylist.find(x => x.id === city.country);
			a_str = getCountryName(country, usr) + ', ';
		}

		return a_str += getCityName(city, usr);
	}

	function taxMapGetSpeciesName(spec) {
		return getShortSpecName(spec, gStorage.conn.user);
	}

	function mapClick(e) {
		let ft = [];

		e.map.forEachFeatureAtPixel(e.pixel, (feat) => {
			feat.coordinate = e.coordinate;
			ft.push(feat);
		});

		ft.sort((a, b) => {
			return b.get('obs').start_t - a.get('obs').start_t;
		});

		setClickFt({pos: 0, fa: ft});
	}

	function mapRxWs(status, msg) {
		if (status !== COMM_OK || msg.result !== 'OK') {
			console.error('mapRxWs: error ', status, msg);
			return -1;
		}

		if (msg.cmd === 'obs' && msg.op === 'list') {
// 			console.log('mapRxWs, obslist', msg.data);
			if (msg.data.obslist && msg.data.obslist.length) {
				gStorage.taxons.setObsList(msg.data.obslist);
			}
		} else {
			console.error('mapRxWs: unknown command', msg.cmd, msg.op, msg.result);
		}
	}

	function loadObs() {
		if (gStorage.taxons.centerTaxon.t_type !== TT_SPEC && gStorage.taxons.centerTaxon.t_type !== TT_SUBSPEC) {
			gStorage.taxons.setObsList([]);
			return;
		}

		const msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'obs',
			op: 'list',
			data: {
				count: LIST_MAP_MAX_ELEM,
				pos: 0,
				city: [],
				species: [],
				trips: [],
				asso: [],
				tz: -new Date().getTimezoneOffset() * 60,
				sort_by: 'time',
				sort_order: 'desc',
				user: gStorage.conn.user.id, 
			},
		}

		specToMsg(msg, [gStorage.taxons.centerTaxon], gStorage.lists.speclist.list);
		
// 		console.log('loadObs', msg);
		gStorage.conn.ws.send(msg, mapRxWs);
	};

	function proceedDetails(dir) {
		setClickFt({pos: clickFt.pos + dir, fa: clickFt.fa});
	}

	function toggleStyle() {
		let tmpStyle = gStorage.taxons.obsStyle;

		tmpStyle++;
		if (tmpStyle >= mapStyles.length) 
			tmpStyle = 0;

		gStorage.taxons.setObsStyle(tmpStyle);
	}

	const tax = gStorage.taxons.centerTaxon;

	if (gStorage.taxons.tab !== TAB_MAP || !tax || !(tax.t_type === TT_SPEC || tax.t_type === TT_SUBSPEC))
		return null;

	if (!gStorage.taxons.obslist && gStorage.conn.online)
		loadObs();

	return (<div ref={mapMapRef} id="mapContainer" style={{width: '100%', height: gStorage.taxons.mapHeight}}>
			<StyleTglBtn tglStyle={toggleStyle} style={gStorage.taxons.obsStyle} mapMapRef={mapMapRef} mapCtrlRef={mapCtrlRef}/>
			<MapObsPopup map={gStorage.taxons.map} proceedDetails={proceedDetails} pos={clickFt.pos} num={clickFt.fa.length} mapPopRef={mapPopRef}/>
		</div>);

});

export default TaxMap;
